import { useState } from "react";
import { api, type Company, type Direction, type Driver } from "../api";
import BackHeader from "../components/BackHeader";
import CountInput from "../components/CountInput";

export default function RegisterFlow({
  driver,
  direction,
  company,
  onDone,
  onBack,
}: {
  driver: Driver;
  direction: Direction;
  company: Company;
  onDone: (message: string, movementId: number) => void;
  onBack: () => void;
}) {
  const [count, setCount] = useState<number | null>(
    direction === "out" && company.balance > 0 ? company.balance : 1,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = direction === "in" ? "Deliver boxes" : "Pick up boxes";
  const verb = direction === "in" ? "Deliver" : "Pick up";

  const confirm = async () => {
    if (busy || !count) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.register({
        driverId: driver.id,
        companyId: company.id,
        direction,
        count,
      });
      const done = direction === "in" ? "delivered to" : "picked up from";
      onDone(`${count} ${count === 1 ? "box" : "boxes"} ${done} ${company.name}`, result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not register. Try again.");
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-5">
      <BackHeader
        title={title}
        titleClass={direction === "in" ? "text-hot" : "text-honey"}
        onBack={onBack}
      />

      <div className="pop-in flex flex-1 flex-col gap-8">
        <div className="mx-auto flex items-center">
          <img src="/matsentralen.png" alt="Matsentralen" className="h-8" />
        </div>

        <CountInput
          value={count}
          onChange={setCount}
          allPreset={direction === "out" && company.balance > 0 ? company.balance : undefined}
        />

        {direction === "out" && company.balance > 0 && (count ?? 0) > company.balance && (
          <p className="text-center text-sm font-semibold text-ink/60">
            Heads up: {company.name} has {company.balance} registered — this will go below zero.
          </p>
        )}

        {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}

        <button
          onClick={confirm}
          disabled={busy || !count}
          className={`mt-auto h-16 w-full rounded-[14px] text-lg font-bold transition-transform active:scale-[0.98] disabled:opacity-60 ${
            direction === "in" ? "bg-rose-grad text-white shadow-button" : "border border-[#efd9a8] bg-lemon text-[#5c4413] shadow-sm"
          }`}
        >
          {busy
            ? "Saving…"
            : count
              ? `${verb} ${count} ${count === 1 ? "box" : "boxes"}`
              : "Enter box count"}
        </button>
      </div>
    </div>
  );
}
